import { useEffect, useRef, useState } from "react";
import { makeStyles, shorthands, mergeClasses } from "@fluentui/react-components";
import { BRAND } from "./tokens";
import Sidebar, { type SidebarScopeSwitcher } from "./Sidebar";
import type { SectionDef } from "./types";
import { alleDokumente } from "../windowManager/browserFenster/fensterRegister";

/**
 * Das Inhaltsverzeichnis für schmale Darstellungen — derselbe `Sidebar`-Baum,
 * nur hinter einem Knopf.
 *
 * Bewusst **kein** `position: fixed`-Drawer über `inset: 0`: im schwebenden
 * Fenster verdunkelte der den gesamten Bildschirm statt des Fensters. Und
 * bewusst kein Portal: das Panel hängt am sticky Knopf und wandert damit beim
 * Scrollen mit, ohne dass ein Anker-Rect nachgerechnet werden müsste.
 */
const useStyles = makeStyles({
  wrap: {
    position: "sticky",
    top: 0,
    zIndex: 25,
    marginBottom: "12px",
    // Der Knopf klebt oben; der Streifen darunter darf den Text nicht
    // durchscheinen lassen.
    backgroundColor: BRAND.bg,
    paddingTop: "8px",
    paddingBottom: "8px",
  },
  btn: {
    display: "inline-flex",
    alignItems: "center",
    columnGap: "10px",
    cursor: "pointer",
    appearance: "none",
    backgroundColor: BRAND.white,
    backgroundImage: "none",
    ...shorthands.border("1px", "solid", "rgba(45,20,43,0.15)"),
    ...shorthands.borderRadius("8px"),
    color: BRAND.purple,
    fontFamily: BRAND.fontPrimary,
    fontSize: "0.88rem",
    fontWeight: 600,
    paddingTop: "8px",
    paddingBottom: "8px",
    paddingLeft: "12px",
    paddingRight: "14px",
    transitionProperty: "background-color, border-color",
    transitionDuration: "120ms",
    ":hover": {
      backgroundColor: "#f5eef6",
    },
  },
  btnOpen: {
    ...shorthands.borderColor(BRAND.purple),
    backgroundColor: "#f5eef6",
  },
  btnKontext: {
    color: BRAND.muted,
    fontWeight: 400,
  },
  panel: {
    position: "absolute",
    top: "100%",
    left: 0,
    zIndex: 30,
    width: "min(320px, 100%)",
    ...shorthands.borderRadius("10px"),
    ...shorthands.overflow("hidden"),
    boxShadow: "0 18px 40px rgba(20,8,24,0.35)",
  },
});

type Props = {
  sections: SectionDef[];
  currentSectionIdx: number;
  currentSubStepIdx: number;
  onSelect: (sectionIdx: number, subStepIdx: number) => void;
  onSectionSelect?: (sectionIdx: number) => void;
  progressFraction?: number;
  progressDetail?: string;
  eyebrow?: string;
  heading?: string;
  ariaLabel?: string;
  scopeSwitcher?: SidebarScopeSwitcher;
  /** Zweite Zeile am Knopf, z. B. „Schritt 3 von 21 · Hauptgebäude". */
  kontextText?: string;
  /** Maximale Panelhöhe; kommt aus `useTocLayout().availableHeight`. */
  maxHeight?: number;
  /** Beschriftung des Knopfs. */
  label?: string;
  /** Durchgereicht an die `Sidebar` — siehe dort. */
  farbschema?: "abschnitte" | "neutral";
};

const TocMenu = ({
  sections,
  currentSectionIdx,
  currentSubStepIdx,
  onSelect,
  onSectionSelect,
  progressFraction,
  progressDetail,
  eyebrow,
  heading,
  ariaLabel,
  scopeSwitcher,
  kontextText,
  maxHeight,
  label = "Inhaltsverzeichnis",
  farbschema,
}: Props) => {
  const styles = useStyles();
  const [open, setOpen] = useState(false);
  const wrapRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!open) return;
    const aufKlick = (e: MouseEvent) => {
      if (!wrapRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const aufTaste = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    alleDokumente.addEventListener("pointerdown", aufKlick);
    alleDokumente.addEventListener("keydown", aufTaste);
    return () => {
      alleDokumente.removeEventListener("pointerdown", aufKlick);
      alleDokumente.removeEventListener("keydown", aufTaste);
    };
  }, [open]);

  const panelHoehe = Math.max(240, Math.min(maxHeight ?? 560, 560));

  return (
    <div className={styles.wrap} ref={wrapRef}>
      <button
        type="button"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
        className={mergeClasses(styles.btn, open && styles.btnOpen)}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          aria-hidden="true"
        >
          <line x1="3" y1="6" x2="21" y2="6" />
          <line x1="3" y1="12" x2="21" y2="12" />
          <line x1="3" y1="18" x2="21" y2="18" />
        </svg>
        <span>{label}</span>
        {kontextText && <span className={styles.btnKontext}>· {kontextText}</span>}
      </button>
      {open && (
        <div className={styles.panel} role="dialog" aria-label={ariaLabel ?? label}>
          <Sidebar
            sections={sections}
            currentSectionIdx={currentSectionIdx}
            currentSubStepIdx={currentSubStepIdx}
            onSelect={(s, ss) => {
              setOpen(false);
              onSelect(s, ss);
            }}
            onSectionSelect={
              onSectionSelect
                ? (s) => {
                    setOpen(false);
                    onSectionSelect(s);
                  }
                : undefined
            }
            progressFraction={progressFraction}
            progressDetail={progressDetail}
            eyebrow={eyebrow}
            heading={heading}
            ariaLabel={ariaLabel}
            scopeSwitcher={scopeSwitcher}
            maxHeight={panelHoehe}
            farbschema={farbschema}
          />
        </div>
      )}
    </div>
  );
};

export default TocMenu;
